import type { StateService } from '@uirouter/core';
import * as angular from 'angular';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { AccountResource, ReportResource, ReportRunResource } from '@mode/legacy/data-access-model-js';
import { ModelState, QueryRunState, QueryState } from '@mode/legacy/types-app-state';
import { DataViewTypes } from '@mode/shared/contract-common';
import { errorGif } from '@mode/shared/ui-images';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { formatBytes, isPresent } from '@mode/shared/util-js';
import { DataViewFacade } from '@mode/switch-views/contract';
import { MixableBaseController } from '../../common/component';
import { BROWSER_LIMIT } from '../../common/constants';
import { accountContextPlanCode, transientSelectedQueryTokenSelector } from '../../reducers/selectors';
import { ReportRunDataService } from '../../services/report-run-service';
import { ReportSaveService } from '../../services/report-save-service';
import ViewService from '../../services/view-service';
import template from './query-execution-result.html';

// (number of rows returned / (PCT_OVERAGE*1.1)), rounded down to the nearest 1,000 (eg, 128,000; 52,000).
// PCT_OVERAGE = uncompressed size of result size / 15mb
function calculateSuggestedRows(actualRowCount: number, dataSize: number): string {
  const PCT_OVERAGE = (dataSize / BROWSER_LIMIT) * 1.1;
  return Math.round(actualRowCount / PCT_OVERAGE).toPrecision(2);
}

interface ReportRunParams {
  token: string;
  selectedText: string;
  runAll: boolean;
}

class QueryExecutionResultController extends MixableBaseController {
  static $inject = [
    '$state',
    '$scope',
    '$rootScope',
    '$sce',
    'ReportSaveService',
    'ViewService',
    'ReportRunDataService',
    'RxStoreService',
    'BugsnagService',
    'DataViewFacade',
  ];

  public queryRun: QueryRunState; // used in template
  public reportRun: ReportRunResource;
  public suggestedRows: string;
  public errorImage: string;
  public report: ReportResource | undefined;
  public dataset: Readonly<ModelState>;
  public billingPlanCode$: Observable<string>;
  public errorLineHtml: string | any; //trustAsHtml -> any
  public account: AccountResource;
  public isDbResultOversized = false;

  public errorText: string;
  public errorResolutionText: string;
  public resolutionLink: string;

  public emptyDataset = false;
  public isLongRunningQuery = false;
  public isDatasetReport = false;
  public queryToken?: string;
  public queryToken$: Observable<string>;
  public switchViewToken$: Observable<string>;
  public switchViewQueryRunState: DataViewTypes.QueryRunStatus;
  public switchViewQueryRunState$: Observable<DataViewTypes.QueryRunStatus>;
  public lastSuccessfulRunAt$: Observable<string>;
  public executionTime$: Observable<string>;
  public showLoader$: Observable<boolean>;
  public showFlamingoGridContainer$: Observable<boolean>;
  public isFallbackData$: Observable<boolean>;
  public bytesSize$: Observable<string>;
  public isPurged$!: Observable<boolean>;

  header = 'The latest data was removed';
  subtext = 'This was a result of your Workspace’s data retention policy. Run again to load new results.';
  icon = 'illo-air-ballon';

  private query: QueryState;
  private rawBytesSize$: Observable<number>;
  private queryTokenBinding$: BehaviorSubject<string>;
  private accountId: string;
  private reportId: string;

  constructor(
    private $state: StateService,
    private $scope: angular.IScope,
    private $rootScope: angular.IRootScopeService,
    private $sce: angular.ISCEService,
    private reportSaveService: ReportSaveService,
    private viewService: ViewService,
    private reportRunDataService: ReportRunDataService,
    private rxStore: AppStore,
    private bugsnag: BugsnagService,
    private dataViewFacade: DataViewFacade
  ) {
    super();
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.queryToken && this.queryTokenBinding$) {
      this.queryTokenBinding$.next(this.queryToken);
    }
  }

  $onInit() {
    this.accountId = this.$state.params.ownerId;
    this.reportId = this.$state.params.reportId;

    this.errorImage = errorGif;

    this.$scope.$on('report.runAll', () => this.createReportRun({ token: '', selectedText: '', runAll: true }));

    // Support queryToken from binding
    this.queryTokenBinding$ = new BehaviorSubject(this.queryToken);
    this.queryToken$ = this.queryTokenBinding$.pipe(
      switchMap((queryToken) =>
        queryToken == null
          ? // Existing implementation selects queryToken from the store
            this.rxStore.select(transientSelectedQueryTokenSelector)
          : // While the report view provides it as a binding
            of(queryToken)
      )
    );

    // For use in the DatasetReport context.
    this.switchViewToken$ = this.queryToken$.pipe(
      filter(isPresent),
      switchMap((queryToken) => this.dataViewFacade.createSwitchViewTokenByQueryToken(queryToken))
    );

    // This queryRunState represents the appropriate state as represented by the switchView resolution flow.
    // Once this value turns to success, the plan will be dispatched, removing old results from the plan id in state.
    this.switchViewQueryRunState$ = this.switchViewToken$.pipe(
      filter(isPresent),
      switchMap((switchViewToken) => this.dataViewFacade.getQueryRunStateBySwitchViewToken(switchViewToken)),
      takeUntil(this.destroyed$)
    );
    this.switchViewQueryRunState$.subscribe((switchViewQueryRunState) => {
      this.$scope.$evalAsync(() => (this.switchViewQueryRunState = switchViewQueryRunState));
    });

    this.rawBytesSize$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getBytesSizeBySwitchViewToken(switchViewToken))
    );

    this.showLoader$ = combineLatest([this.switchViewQueryRunState$, this.rawBytesSize$]).pipe(
      map(([state, byteSize]) => {
        const dataIsAvailable = byteSize > 0;
        const queryRunStateSucceededOrRunning = [
          DataViewTypes.QueryRunStatus.Succeeded,
          DataViewTypes.QueryRunStatus.Pending,
          DataViewTypes.QueryRunStatus.Enqueued,
        ].includes(state);
        return !(queryRunStateSucceededOrRunning && dataIsAvailable);
      }),
      takeUntil(this.destroyed$)
    );
    this.showFlamingoGridContainer$ = combineLatest([this.switchViewQueryRunState$]).pipe(
      map(([queryRunState]) => {
        return (
          queryRunState === DataViewTypes.QueryRunStatus.Succeeded ||
          queryRunState === DataViewTypes.QueryRunStatus.Pending
        );
      })
    );

    this.executionTime$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getExecutionTimeBySwitchViewToken(switchViewToken))
    );

    this.bytesSize$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) =>
        this.dataViewFacade
          .getBytesSizeBySwitchViewToken(switchViewToken)
          .pipe(map((bytesSize) => formatBytes(bytesSize)))
      )
    );

    this.isFallbackData$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getIsFallbackDataBySwitchViewToken(switchViewToken))
    );

    this.isPurged$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getIsPurgedBySwitchViewToken(switchViewToken))
    );

    this.lastSuccessfulRunAt$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getDatasetResultCompletedAtBySwitchViewToken(switchViewToken))
    );

    this.$scope.$on('report.create', (event, data = { selectedText: '' }) => {
      // Pulling query token directly from the observable, since there might
      // be a race condition where the query object isn't present yet. Ideally,
      // we will stop relying on scope.broadcast for this.
      this.queryToken$.pipe(first()).subscribe((queryToken) => {
        if (queryToken != null) {
          const { token = queryToken, selectedText } = data;
          this.createReportRun({ token, selectedText, runAll: false });
        } else {
          this.bugsnag.notify(new Error('Unable to get selected query token'));
        }
      });
    });

    this.reportRunDataService.selectedQueryReportRunState$.pipe(takeUntil(this.destroyed$)).subscribe((state) => {
      this.reportRun = state.reportRun && state.reportRun.resource;
      this.query = state.query;
      this.queryRun = state.queryRun;
      this.dataset = state.modelState;
      this.emptyDataset = Object.keys(this.dataset).length <= 0;

      if (this.dataset && this.dataset.oversized === true) {
        const { count, content_length } = this.dataset;
        this.suggestedRows = calculateSuggestedRows(count, content_length);
      }

      // Queryrun can be undefined at various times
      if (this.queryRun) {
        this.isDbResultOversized = this.queryRun.isDbResultOversized;
        this.errorText = this.queryRun.errorText;
        this.errorResolutionText = this.queryRun.errorResolutionText;
        this.resolutionLink = this.queryRun.resolutionLink;
      }

      this.errorLineHtml = (state.queryRun && this.$sce.trustAsHtml(state.queryRun.errorLineHtml)) || '';
    });

    this.billingPlanCode$ = this.rxStore.selectNoEmpty(accountContextPlanCode);

    this.reportRunDataService.queryRunComplete$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryRun) => this.viewService.queryRunComplete(queryRun.query_token));

    this.report ? this.reportSaveService.copyReport(this.report) : '';
    this.$rootScope.$broadcast('preview.updateHtmlView');
  }

  private createReportRun({ token, selectedText, runAll }: ReportRunParams) {
    this.reportRunDataService.executeReportRun(runAll ? undefined : token, selectedText);
  }

  get isQueryRunResultTooLarge(): boolean {
    return this.queryRun && this.queryRun.errorCode != null && this.queryRun.errorCode === 'QueryRunResultTooLarge';
  }

  retry() {
    if (!this.reportRun) {
      return;
    }
    this.reportRunDataService.pollReportRun(this.reportRun.token);
  }

  // Navigate to notebook
  goToNotebook(): void {
    this.viewService.toggleNotebookShown(true);
    this.viewService.goToNotebook();
  }

  secondaryHeaderToggle() {
    /*
    When the dataview component is maximized
    viewService.maximize acts as a toggle function */
    this.viewService.maximize('results', this.queryToken);
  }
}

export const queryExecutionResultComponent: angular.IComponentOptions = {
  bindings: {
    report: '<',
    account: '<',
    csvLink: '<',
    sizeGridToFit: '<',
    queryToken: '<?',
    isDatasetReport: '<?',
  },
  controller: QueryExecutionResultController,
  controllerAs: 'ctrl',
  template,
};
